/**
 * Post-checkout landing page (`/billing/success`).
 *
 * Stripe redirects here after a successful Checkout — typically before
 * the corresponding webhook has landed on our backend. We poll
 * GET /billing/status for ~10s waiting for the tier to flip to
 * `active`. If it never does (webhook lag, network blip), show a
 * graceful message + CTA back to Settings where they can verify.
 *
 * The `session_id` query param is set by Stripe's success_url template.
 * We don't currently use it server-side (Stripe Checkout sessions are
 * idempotent on completion), but it's worth keeping in the URL for
 * Sentry breadcrumbs / debugging.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getBillingStatus, type BillingStatus } from "@/features/billing/lib/billing";

type PollState =
  | { kind: "polling"; attempts: number }
  | { kind: "active"; status: BillingStatus }
  | { kind: "timeout"; status: BillingStatus | null };

export function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState<PollState>({ kind: "polling", attempts: 0 });

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 10; // ~10 seconds with 1s spacing
    const interval = 1000;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const s = await getBillingStatus();
        if (cancelled) return;
        if (s.subscriptionStatus === "active" || s.subscriptionStatus === "past_due") {
          setState({ kind: "active", status: s });
          return;
        }
        if (attempt >= maxAttempts) {
          setState({ kind: "timeout", status: s });
          return;
        }
        setState({ kind: "polling", attempts: attempt });
        setTimeout(poll, interval);
      } catch {
        if (cancelled) return;
        if (attempt >= maxAttempts) {
          setState({ kind: "timeout", status: null });
          return;
        }
        setTimeout(poll, interval);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-16 text-center space-y-6">
      {state.kind === "polling" && (
        <>
          <div className="mx-auto w-12 h-12 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <h1 className="text-2xl font-semibold text-text tracking-tight">
            Wrapping up your subscription…
          </h1>
          <p className="text-sm text-text-muted">
            Confirming with Stripe (attempt {state.attempts} of 10). This
            usually takes a few seconds.
          </p>
        </>
      )}

      {state.kind === "active" && (
        <>
          <div className="mx-auto w-12 h-12 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center">
            <span className="text-accent text-xl">✓</span>
          </div>
          <h1 className="text-2xl font-semibold text-text tracking-tight">
            Welcome to Holdem Drills Premium
          </h1>
          <p className="text-sm text-text-muted">
            {state.status.isFounder
              ? "You're locked into Founder pricing: for life."
              : "Your subscription is active. Drill away."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/drills")}
            className="w-full py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]"
          >
            Start drilling
          </button>
        </>
      )}

      {state.kind === "timeout" && (
        <>
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <span className="text-amber-400 text-xl">…</span>
          </div>
          <h1 className="text-2xl font-semibold text-text tracking-tight">
            Stripe is still processing
          </h1>
          <p className="text-sm text-text-muted">
            Your checkout completed but the activation hasn't reached us yet.
            This is usually a brief webhook delay. Check Settings in a minute
            to confirm: or refresh this page.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate("/settings")}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border text-text-muted text-sm font-medium hover:text-text hover:border-text-muted/40 transition"
            >
              Go to Settings
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex-1 px-4 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 transition"
            >
              Refresh
            </button>
          </div>
        </>
      )}

      {sessionId && (
        <p className="text-[10px] text-text-muted/40 font-mono pt-4">
          session: {sessionId.slice(0, 20)}…
        </p>
      )}
    </div>
  );
}
